import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TruncatedText } from '@/shared/ui';
import type { ChartBindingValue, CustomChartConversationItem, CustomChartValueType } from '@/entities/dashboards';
import { cn, preventShiftSelection } from '@/shared/lib';
import { getConversationLinkProps } from '../../conversationLink';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS, chartNumberDigits } from '../../utils';
import { groupChartItems } from '../chartElements';
import { chartDatePrecision, shownChartValue } from '../chartValues';
import { ChartGroupSection } from './ChartGroupSection';
import { ChartCardShell } from '../ChartCardShell';

type Props = {
  title: string;
  items: CustomChartConversationItem[];
  /** Тип величины строки: по нему величина-дата показывается датой, а число — числом */
  valueType: CustomChartValueType;
  /**
   * Открытие диалога; `openInNewTab` — пользователь кликнул с shift. Не задано — строки нажатий
   * не принимают и адреса диалога не несут.
   */
  onOpenConversation?: (conversationId: number, openInNewTab: boolean) => void;
  /** Выбор строки наполняет связанную диаграмму; задан — выбор заменяет переход к диалогу */
  onSelectElement?: (value: ChartBindingValue) => void;
  /** Выбранная строка — она подсвечена, повторный выбор снимает её */
  selectedElement?: ChartBindingValue;
};

/** Начертание строки списка: у строки с диалогом добавляется отклик на наведение. */
const ROW_CLASS = 'flex items-center gap-3 rounded-lg border bg-card p-3 no-underline text-inherit';

/**
 * Список диалогов: перечень отдельных разговоров с величиной каждого.
 *
 * Строка с разговором ведёт в него ссылкой — правило перехода общее для дашборда, см.
 * {@link getConversationLinkProps}. Строка, за которой разговор не назван, остаётся обычной
 * строкой: переходить по ней некуда.
 *
 * У списка, выбор в котором наполняет связанную диаграмму, строка выбирает разговор для разбора, а
 * не уходит в него: разбор ведётся на дашборде, и это последняя его ступень — оценка выбранного
 * диалога. Разговор при этом опознаётся своим идентификатором, а не названием строки.
 *
 * Строки, отнесённые выборкой к группам, собраны в озаглавленные группы в порядке их первого
 * появления.
 *
 * С погашенным переходом ссылок в списке нет вовсе: в режиме правки состава уходить с дашборда
 * некуда, и адрес диалога у строки только обманывал бы.
 */
export const ConversationListChartView: FC<Props> = ({
  title,
  items,
  valueType,
  onOpenConversation,
  onSelectElement,
  selectedElement,
}) => {
  const { t } = useTranslation('qualityDashboard');

  // Точность времени берётся по величинам всех строк сразу — соседние строки написаны одинаково.
  const precision = chartDatePrecision(
    items.map((item) => item.value),
    valueType,
  );
  const digits = chartNumberDigits(items.map((item) => item.value));

  const groups = groupChartItems(items);

  return (
    <ChartCardShell title={title}>
      <div className="flex flex-col gap-4">
        {groups.map((group) => (
          <ChartGroupSection key={group.title} title={group.title}>
            {group.items.map((item, index) => {
              const { conversationId } = item;
              const value = shownChartValue(item.value, valueType, precision, digits);
              const content = (
                <>
                  <TruncatedText className="min-w-0 flex-1 text-sm">{item.title}</TruncatedText>
                  <span className="text-sm font-bold whitespace-nowrap">{value}</span>
                </>
              );

              if (onSelectElement && conversationId !== null) {
                const isDimmed = selectedElement !== undefined && selectedElement !== conversationId;
                return (
                  <div
                    key={conversationId}
                    role="button"
                    tabIndex={-1}
                    aria-label={t('presets.chart.selectElement')}
                    className={cn(ROW_CLASS, DASHBOARD_CLICKABLE_BLOCK_CLASS, isDimmed && 'opacity-60')}
                    onMouseDown={preventShiftSelection}
                    onClick={() => onSelectElement(conversationId)}
                  >
                    {content}
                  </div>
                );
              }

              if (conversationId === null || !onOpenConversation) {
                // Ключ по месту в группе: строка без разговора ничем другим не адресуется, а с
                // погашенным переходом по месту адресуются и все прочие строки.
                return (
                  <div key={`${item.title}-${index}`} className={ROW_CLASS}>
                    {content}
                  </div>
                );
              }

              return (
                <a
                  key={conversationId}
                  {...getConversationLinkProps(conversationId, onOpenConversation)}
                  className={cn(ROW_CLASS, DASHBOARD_CLICKABLE_BLOCK_CLASS)}
                >
                  {content}
                </a>
              );
            })}
          </ChartGroupSection>
        ))}
      </div>
    </ChartCardShell>
  );
};
